/**
 * Inoculation Store
 * 
 * Zustand store for managing inoculation operations:
 * - Exploit vector management
 * - Inoculation job tracking
 * - Progress updates
 * - Stats
 * 
 * Peer reviewed: Gemini 2.5 Pro (9/10)
 */

import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type {
  ExploitVector,
  InoculationJob,
  InoculationStats,
  // InoculationStatus is exported but used indirectly via InoculationJob
  VulnerableDevice,
  ExploitVectorFilters,
  DeviceInoculationResult,
  InoculationProgressUpdate,
  ExploitSeverity,
} from '../types/inoculation';

// ============================================================================
// State Interface
// ============================================================================

interface InoculationState {
  // Exploit Vectors
  exploitVectors: Map<string, ExploitVector>;
  selectedVectorId: string | null;
  
  // Vulnerable Devices (fetched per vector)
  vulnerableDevices: Map<string, VulnerableDevice[]>; // vectorId -> devices
  selectedDeviceIds: Set<string>;
  
  // Jobs
  jobs: Map<string, InoculationJob>;
  activeJobId: string | null;
  
  // Stats
  stats: InoculationStats | null;
  
  // Filters
  filters: ExploitVectorFilters;
  
  // UI State
  isLoading: boolean;
  isExecuting: boolean;
  error: string | null;
}

// ============================================================================
// Actions Interface
// ============================================================================

interface InoculationActions {
  // Exploit Vectors
  setExploitVectors: (vectors: ExploitVector[]) => void;
  updateExploitVector: (id: string, updates: Partial<ExploitVector>) => void;
  selectVector: (id: string | null) => void;
  
  // Vulnerable Devices
  setVulnerableDevices: (vectorId: string, devices: VulnerableDevice[]) => void;
  selectDevice: (deviceId: string) => void;
  deselectDevice: (deviceId: string) => void;
  selectAllDevices: () => void;
  deselectAllDevices: () => void;
  toggleDevice: (deviceId: string) => void;
  
  // Jobs
  setJobs: (jobs: InoculationJob[]) => void;
  addJob: (job: InoculationJob) => void;
  updateJob: (id: string, updates: Partial<InoculationJob>) => void;
  setActiveJob: (id: string | null) => void;
  cancelJob: (id: string) => void;
  
  // Progress
  handleProgressUpdate: (update: InoculationProgressUpdate) => void;
  updateDeviceResult: (jobId: string, result: DeviceInoculationResult) => void;
  
  // Stats
  setStats: (stats: InoculationStats) => void;
  
  // Filters
  setFilters: (filters: Partial<ExploitVectorFilters>) => void;
  resetFilters: () => void;
  
  // Selectors
  getFilteredVectors: () => ExploitVector[];
  getSelectedDevices: () => VulnerableDevice[];
  getPendingJobs: () => InoculationJob[];
  getActiveJobs: () => InoculationJob[];
  getCompletedJobs: () => InoculationJob[];
  
  // Loading States
  setIsLoading: (isLoading: boolean) => void;
  setIsExecuting: (isExecuting: boolean) => void;
  setError: (error: string | null) => void;
  
  // Reset
  reset: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialFilters: ExploitVectorFilters = {
  severities: [],
  hasPatch: null,
  searchQuery: '',
  sortBy: 'severity',
  sortOrder: 'desc',
};

const initialState: InoculationState = {
  exploitVectors: new Map(),
  selectedVectorId: null,
  vulnerableDevices: new Map(),
  selectedDeviceIds: new Set(),
  jobs: new Map(),
  activeJobId: null,
  stats: null,
  filters: initialFilters,
  isLoading: false,
  isExecuting: false,
  error: null,
};

// ============================================================================
// Helper Functions
// ============================================================================

const severityOrder: Record<ExploitSeverity, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

function sortVectors(vectors: ExploitVector[], sortBy: string, sortOrder: string): ExploitVector[] {
  return [...vectors].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'severity':
        comparison = severityOrder[a.severity] - severityOrder[b.severity];
        break;
      case 'deviceCount':
        comparison = a.vulnerableDeviceCount - b.vulnerableDeviceCount;
        break;
      case 'lastSeen':
        comparison = new Date(a.lastSeen).getTime() - new Date(b.lastSeen).getTime();
        break;
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      default:
        comparison = 0;
    }
    
    return sortOrder === 'desc' ? -comparison : comparison;
  });
}

// ============================================================================
// Store Creation
// ============================================================================

export const useInoculationStore = create<InoculationState & InoculationActions>()(
  devtools(
    subscribeWithSelector(
      immer((set, get) => ({
        ...initialState,

        // ----------------------------------------------------------------------
        // Exploit Vectors
        // ----------------------------------------------------------------------
        
        setExploitVectors: (vectors: ExploitVector[]) => {
          set((state) => {
            state.exploitVectors.clear();
            vectors.forEach((v) => state.exploitVectors.set(v.id, v));
          });
        },
        
        updateExploitVector: (id: string, updates: Partial<ExploitVector>) => {
          set((state) => {
            const existing = state.exploitVectors.get(id);
            if (existing) {
              state.exploitVectors.set(id, { ...existing, ...updates });
            }
          });
        },
        
        selectVector: (id: string | null) => {
          set((state) => {
            state.selectedVectorId = id;
            state.selectedDeviceIds.clear();
          });
        },

        // ----------------------------------------------------------------------
        // Vulnerable Devices
        // ----------------------------------------------------------------------
        
        setVulnerableDevices: (vectorId: string, devices: VulnerableDevice[]) => {
          set((state) => {
            state.vulnerableDevices.set(vectorId, devices);
          });
        },
        
        selectDevice: (deviceId: string) => {
          set((state) => {
            state.selectedDeviceIds.add(deviceId);
          });
        },
        
        deselectDevice: (deviceId: string) => {
          set((state) => {
            state.selectedDeviceIds.delete(deviceId);
          });
        },
        
        selectAllDevices: () => {
          const selectedVectorId = get().selectedVectorId;
          if (!selectedVectorId) {return;}
          
          const devices = get().vulnerableDevices.get(selectedVectorId) || [];
          set((state) => {
            devices.forEach((d) => state.selectedDeviceIds.add(d.id));
          });
        },
        
        deselectAllDevices: () => {
          set((state) => {
            state.selectedDeviceIds.clear();
          });
        },
        
        toggleDevice: (deviceId: string) => {
          set((state) => {
            if (state.selectedDeviceIds.has(deviceId)) {
              state.selectedDeviceIds.delete(deviceId);
            } else {
              state.selectedDeviceIds.add(deviceId);
            }
          });
        },

        // ----------------------------------------------------------------------
        // Jobs
        // ----------------------------------------------------------------------
        
        setJobs: (jobs: InoculationJob[]) => {
          set((state) => {
            state.jobs.clear();
            jobs.forEach((j) => state.jobs.set(j.id, j));
          });
        },
        
        addJob: (job: InoculationJob) => {
          set((state) => {
            state.jobs.set(job.id, job);
          });
        },
        
        updateJob: (id: string, updates: Partial<InoculationJob>) => {
          set((state) => {
            const existing = state.jobs.get(id);
            if (existing) {
              state.jobs.set(id, { ...existing, ...updates });
            }
          });
        },
        
        setActiveJob: (id: string | null) => {
          set((state) => {
            state.activeJobId = id;
          });
        },
        
        cancelJob: (id: string) => {
          set((state) => {
            const job = state.jobs.get(id);
            if (job && (job.status === 'pending' || job.status === 'in-progress')) {
              job.status = 'cancelled';
              job.completedAt = new Date().toISOString();
            }
          });
        },

        // ----------------------------------------------------------------------
        // Progress Updates
        // ----------------------------------------------------------------------
        
        handleProgressUpdate: (update: InoculationProgressUpdate) => {
          set((state) => {
            const job = state.jobs.get(update.jobId);
            if (job) {
              job.status = update.status;
              // Update progress object based on percentage
              if (job.progress) {
                const progressPercent = update.progress / 100;
                const completed = Math.floor(job.progress.totalCount * progressPercent);
                job.progress.completedCount = completed;
              }
              if (update.deviceResults) {
                job.deviceResults = update.deviceResults;
              }
              if (update.error) {
                // Handle error but don't overwrite existing error
              }
              if (update.status === 'completed' || update.status === 'failed' || update.status === 'completed-with-errors') {
                job.completedAt = new Date().toISOString();
              }
            }
          });
        },
        
        updateDeviceResult: (jobId: string, result: DeviceInoculationResult) => {
          set((state) => {
            const job = state.jobs.get(jobId);
            if (job) {
              const index = job.deviceResults.findIndex((d) => d.deviceId === result.deviceId);
              if (index !== -1) {
                job.deviceResults[index] = result;
              } else {
                job.deviceResults.push(result);
              }
              
              // Update progress counts
              const completed = job.deviceResults.filter((d) => d.status === 'success').length;
              const failed = job.deviceResults.filter((d) => d.status === 'failed').length;
              const skipped = job.deviceResults.filter((d) => d.status === 'skipped').length;
              
              job.progress.completedCount = completed;
              job.progress.failedCount = failed;
              job.progress.skippedCount = skipped;
              job.progress.totalCount = job.targetDeviceIds.length;
              job.progress.currentDeviceName = result.deviceName;
            }
          });
        },

        // ----------------------------------------------------------------------
        // Stats
        // ----------------------------------------------------------------------
        
        setStats: (stats: InoculationStats) => {
          set((state) => {
            state.stats = stats;
          });
        },

        // ----------------------------------------------------------------------
        // Filters
        // ----------------------------------------------------------------------
        
        setFilters: (filters: Partial<ExploitVectorFilters>) => {
          set((state) => {
            Object.assign(state.filters, filters);
          });
        },
        
        resetFilters: () => {
          set((state) => {
            state.filters = initialFilters;
          });
        },

        // ----------------------------------------------------------------------
        // Selectors
        // ----------------------------------------------------------------------
        
        getFilteredVectors: () => {
          const { exploitVectors, filters } = get();
          let vectors = Array.from(exploitVectors.values());
          
          // Filter by severity
          if (filters.severities.length > 0) {
            vectors = vectors.filter((v) => filters.severities.includes(v.severity));
          }
          
          // Filter by patch availability
          if (filters.hasPatch !== null) {
            vectors = vectors.filter((v) => v.patchAvailable === filters.hasPatch);
          }
          
          // Filter by search query
          if (filters.searchQuery) {
            const query = filters.searchQuery.toLowerCase();
            vectors = vectors.filter(
              (v) =>
                v.name.toLowerCase().includes(query) ||
                v.cveId?.toLowerCase().includes(query) ||
                v.description.toLowerCase().includes(query)
            );
          }
          
          // Sort
          return sortVectors(vectors, filters.sortBy, filters.sortOrder);
        },
        
        getSelectedDevices: () => {
          const { selectedVectorId, vulnerableDevices, selectedDeviceIds } = get();
          if (!selectedVectorId) {return [];}
          
          const devices = vulnerableDevices.get(selectedVectorId) || [];
          return devices.filter((d) => selectedDeviceIds.has(d.id));
        },
        
        getPendingJobs: () => {
          return Array.from(get().jobs.values()).filter((j) => j.status === 'pending');
        },
        
        getActiveJobs: () => {
          return Array.from(get().jobs.values()).filter((j) => j.status === 'in-progress');
        },
        
        getCompletedJobs: () => {
          return Array.from(get().jobs.values())
            .filter((j) => ['completed', 'completed-with-errors', 'failed', 'cancelled'].includes(j.status))
            .sort((a, b) => new Date(b.completedAt || 0).getTime() - new Date(a.completedAt || 0).getTime());
        },

        // ----------------------------------------------------------------------
        // Loading States
        // ----------------------------------------------------------------------
        
        setIsLoading: (isLoading: boolean) => {
          set((state) => {
            state.isLoading = isLoading;
          });
        },
        
        setIsExecuting: (isExecuting: boolean) => {
          set((state) => {
            state.isExecuting = isExecuting;
          });
        },
        
        setError: (error: string | null) => {
          set((state) => {
            state.error = error;
            state.isLoading = false;
            state.isExecuting = false;
          });
        },

        // ----------------------------------------------------------------------
        // Reset
        // ----------------------------------------------------------------------
        
        reset: () => {
          set(() => ({ ...initialState }));
        },
      }))
    ),
    { name: 'inoculation-store' }
  )
);

// ============================================================================
// Selectors
// ============================================================================

export const selectExploitVectors = (state: InoculationState) => state.exploitVectors;
export const selectSelectedVector = (state: InoculationState & InoculationActions) =>
  state.selectedVectorId ? state.exploitVectors.get(state.selectedVectorId) : undefined;
export const selectVulnerableDevices = (state: InoculationState) => state.vulnerableDevices;
export const selectJobs = (state: InoculationState) => state.jobs;
export const selectActiveJob = (state: InoculationState & InoculationActions) =>
  state.activeJobId ? state.jobs.get(state.activeJobId) : undefined;
export const selectStats = (state: InoculationState) => state.stats;
export const selectFilters = (state: InoculationState) => state.filters;
export const selectIsLoading = (state: InoculationState) => state.isLoading;
export const selectIsExecuting = (state: InoculationState) => state.isExecuting;
export const selectError = (state: InoculationState) => state.error;
export const selectSelectedDeviceIds = (state: InoculationState) => state.selectedDeviceIds;

